import { ROUTES } from "@config";
import { useCallback, useEffect, useMemo, useSyncExternalStore } from "react";
import {
  matchPath,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useStore } from "src/stores";

const GLOBAL_DEVICE_ROUTE_PATTERNS = [
  ROUTES.DEVICE_OVERVIEW,
  ROUTES.TRACEABILITY_MATRIX,
  ROUTES.ASSISTANT,
  ROUTES.ASSISTANT_DU_CONFIGURE,
  ROUTES.ASSISTANT_DU_LIST_OF_DOCS_TO_CHANGE,
  ROUTES.ASSISTANT_DU_MULTI_STEP_FORM,
  ROUTES.ASSISTANT_DU_GENERATING_DOCS,
  ROUTES.ASSISTANT_DU_UPDATE_DOCS,
  ROUTES.TERMINAL,
];

const DOCUMENT_ROUTE_PATTERNS = [ROUTES.QMS, ROUTES.QMS_OPEN_DOC];

const matchesAnyRoute = (pathname: string, patterns: string[]) =>
  patterns.filter(Boolean).some((pattern) =>
    Boolean(
      matchPath(
        {
          path: pattern,
          end: true,
        },
        pathname
      )
    )
  );

export const useGlobalDeviceContext = () => {
  const { deviceStore } = useStore();
  const { orgId = "", deviceId: routeDeviceId = "" } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const queryDeviceId = searchParams.get("deviceId");
  const storedActiveGlobalDeviceId = useSyncExternalStore(
    useCallback(
      (listener: () => void) => deviceStore.subscribeToGlobalDeviceContext(listener),
      [deviceStore]
    ),
    useCallback(
      () => (orgId ? deviceStore.getActiveGlobalDeviceId(orgId) : null),
      [deviceStore, orgId]
    ),
    () => null
  );

  const isDocumentRoute = matchesAnyRoute(location.pathname, DOCUMENT_ROUTE_PATTERNS);
  const isGlobalDeviceRoute = matchesAnyRoute(
    location.pathname,
    GLOBAL_DEVICE_ROUTE_PATTERNS
  );
  const persistedActiveGlobalDeviceId = storedActiveGlobalDeviceId ?? null;
  const transientRouteDeviceId = routeDeviceId || queryDeviceId || null;
  const activeGlobalDeviceId =
    persistedActiveGlobalDeviceId ??
    (isGlobalDeviceRoute && routeDeviceId ? routeDeviceId : null);
  const hasActiveGlobalDevice = Boolean(activeGlobalDeviceId);

  useEffect(() => {
    if (
      !orgId ||
      !routeDeviceId ||
      !isGlobalDeviceRoute ||
      persistedActiveGlobalDeviceId
    ) {
      return;
    }

    deviceStore.setActiveGlobalDeviceId(orgId, routeDeviceId);
  }, [
    deviceStore,
    isGlobalDeviceRoute,
    orgId,
    persistedActiveGlobalDeviceId,
    routeDeviceId,
  ]);

  const isMismatchedDocumentRoute = Boolean(
    activeGlobalDeviceId &&
      isDocumentRoute &&
      routeDeviceId &&
      activeGlobalDeviceId !== routeDeviceId
  );

  const effectiveDrawerDeviceId = useMemo(() => {
    if (isMismatchedDocumentRoute) {
      return activeGlobalDeviceId;
    }

    return activeGlobalDeviceId ?? null;
  }, [activeGlobalDeviceId, isMismatchedDocumentRoute]);
  const hasDrawerDeviceContext = Boolean(effectiveDrawerDeviceId);

  const setActiveGlobalDeviceId = useCallback(
    (deviceId: string | null) => {
      if (!orgId) {
        return;
      }

      deviceStore.setActiveGlobalDeviceId(orgId, deviceId);
    },
    [deviceStore, orgId]
  );

  const clearActiveGlobalDeviceId = useCallback(() => {
    if (!orgId) {
      return;
    }

    deviceStore.clearActiveGlobalDeviceId(orgId);
  }, [deviceStore, orgId]);

  return {
    activeGlobalDeviceId,
    clearActiveGlobalDeviceId,
    effectiveDrawerDeviceId,
    hasActiveGlobalDevice,
    hasDrawerDeviceContext,
    isDocumentRoute,
    isGlobalDeviceRoute,
    isMismatchedDocumentRoute,
    persistedActiveGlobalDeviceId,
    queryDeviceId,
    routeDeviceId,
    setActiveGlobalDeviceId,
    transientRouteDeviceId,
  };
};
